// RelatedToolsBar — small "Next steps" card row that lives at the bottom
// of upload-heavy tools (AWS File Links, AWS File Links Alternate, …).
// Surfaces the follow-up tools a user is most likely to reach for next
// once they've got an Excel of image links: bulk-download the images,
// or sanity-check that none of the links are broken.
//
// Props:
//   • tools   — override the default tool list (see DEFAULT_TOOLS)
//   • title   — header label (default "Next steps")
//   • layout  — 'wide' (2 cols on sm+) or 'stack' (always 1 col, compact)
//   • subtitle — short prompt under the title; pass '' to hide

import React from 'react';
import { Box, Typography, Paper, ButtonBase } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

const DEFAULT_TOOLS = [
  {
    name:    'Bulk Image Downloader',
    path:    '/dashboard/bulk-image-downloader',
    accent:  '#2563EB',
    Icon:    CloudDownloadRoundedIcon,
    blurb:   "Drop the Excel of AWS links here and it'll fetch every image and ship them back as a single ZIP. Skip the manual click-and-save grind.",
    ctaTag:  'Download all images',
  },
  {
    name:    'Broken Link Checker',
    path:    '/dashboard/broken-link-checker',
    accent:  '#DC2626',
    Icon:    LinkOffRoundedIcon,
    blurb:   "Same Excel, different question — checks every URL and flags 404s, dead hosts, and unreachable files so nothing broken slips into a sales doc.",
    ctaTag:  'Verify links',
  },
];

const RelatedToolsBar = ({
  tools = DEFAULT_TOOLS,
  title = 'Next steps',
  subtitle = 'Once you have your Excel of image links',
  layout = 'wide',
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const isStack = layout === 'stack';

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        p: { xs: 1.75, md: 2 },
        borderRadius: 2.5,
        overflow: 'hidden',
        position: 'relative',
        // Subtle two-stop gradient backdrop — gives the card surface a
        // tiny bit of warmth so it doesn't read as just-another panel.
        background: isDark
          ? 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(37,99,235,0.04) 100%)'
          : 'linear-gradient(135deg, #FAF5FF 0%, #EFF6FF 100%)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}`,
      }}
    >
      {/* Sparkle accent — purely decorative, sits in the corner so the
          eye knows the card is "actionable next steps" not metadata. */}
      <Box
        sx={{
          position: 'absolute',
          top: -14, right: -14,
          width: 80, height: 80,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${tools[0]?.accent || '#7C3AED'}22 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, position: 'relative' }}>
        <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography
          variant="overline"
          sx={{ fontWeight: 800, letterSpacing: 1.1, fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1 }}
        >
          {title}
        </Typography>
        {subtitle && !isStack && (
          <Typography
            variant="caption"
            sx={{
              ml: 'auto', color: 'text.secondary',
              display: { xs: 'none', md: 'block' },
              fontStyle: 'italic',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Tool grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isStack
            ? '1fr'
            : { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: isStack ? 1.25 : 1.75,
        }}
      >
        {tools.map(({ name, path, accent, Icon, blurb, ctaTag }) => (
          <ButtonBase
            key={path}
            focusRipple
            onClick={() => navigate(path)}
            sx={{
              textAlign: 'left',
              display: 'block',
              p: 0,
              borderRadius: 2,
              transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1)',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)'}`,
                transition: 'border-color 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                  borderColor: `${accent}66`,
                  boxShadow: isDark
                    ? `0 8px 20px ${accent}33`
                    : `0 8px 20px ${accent}22`,
                  '& .rtb-arrow-circle': { transform: 'translateX(0)', opacity: 1 },
                  '& .rtb-icon':          { transform: 'scale(1.06) rotate(-3deg)', borderColor: accent },
                  '& .rtb-name':          { color: accent },
                },
                // Top accent rail — micro detail that reads on hover.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0,
                  height: 2,
                  background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                  transform: 'scaleX(0.15)',
                  transformOrigin: 'center',
                  transition: 'transform 320ms cubic-bezier(0.22,1,0.36,1)',
                },
                '&:hover::before': { transform: 'scaleX(1)' },
              }}
            >
              {/* Icon block */}
              <Box
                className="rtb-icon"
                sx={{
                  width: isStack ? 38 : 44,
                  height: isStack ? 38 : 44,
                  flexShrink: 0,
                  borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `linear-gradient(135deg, ${accent}22, ${accent}10)`,
                  color: accent,
                  border: `1px solid ${accent}33`,
                  transition: 'transform 240ms cubic-bezier(0.34,1.56,0.64,1), border-color 240ms ease',
                }}
              >
                <Icon sx={{ fontSize: isStack ? 20 : 22 }} />
              </Box>

              {/* Body */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    className="rtb-name"
                    sx={{
                      fontWeight: 700,
                      fontSize: isStack ? '0.875rem' : '0.95rem',
                      lineHeight: 1.3,
                      color: 'text.primary',
                      transition: 'color 180ms ease',
                      flex: 1, minWidth: 0,
                      // Allow long names to wrap naturally instead of being clipped.
                      wordBreak: 'break-word',
                    }}
                  >
                    {name}
                  </Typography>
                  <Box
                    className="rtb-arrow-circle"
                    sx={{
                      width: 20, height: 20,
                      borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${accent}18`,
                      color: accent,
                      opacity: 0,
                      transform: 'translateX(-6px)',
                      transition: 'opacity 200ms ease, transform 200ms ease',
                      flexShrink: 0,
                      mt: 0.25, // visually centers against the name's cap-height
                    }}
                  >
                    <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} />
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: isStack ? '0.78rem' : '0.82rem',
                    lineHeight: 1.5,
                    mb: 1.25,
                    // Full blurb — no clamping. Cards expand vertically to
                    // fit the entire description text.
                    wordBreak: 'break-word',
                  }}
                >
                  {blurb}
                </Typography>

                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.85, py: 0.2,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    bgcolor: `${accent}14`,
                    color: accent,
                  }}
                >
                  {ctaTag}
                </Typography>
              </Box>
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Paper>
  );
};

export default RelatedToolsBar;
